import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { getExhibitionSettings, getSelfRegistration, selectIsLogoutProcessing } from '@app/app.reducer';
import {
  DynamicFormFields,
  FieldKey,
  FieldType,
  OptionKey,
  PrivacyPolicyCheckboxField
} from '@core/data-access/models/dynamic-form-field/dynamic-form-field.model';
import { FormFactory } from '@core/data-access/models/form/form-mediator.model';
import { FormFields } from '@core/data-access/models/form/form-store.model';
import { FormName } from '@core/data-access/models/form/form.model';
import { DynamicFormFactory } from '@core/data-access/services/dynamic-form-field/dynamic-form-factory.service';
import {
  selectBuyerPerson,
  selectBuyerPrivacyPolicyFormFields
} from '@core/features/buyer-information/store/buyer-information.selectors';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { AppConstants } from '@shared/app-constants';
import { ExhibitionSettingModel } from '@store/customization/customization.interfaces';
import { getIsTranslationLoaded } from '@store/translation/translation.selectors';
import { filter as _filter, head, isEmpty, isNil } from 'lodash';
import { BehaviorSubject, combineLatest, filter, map, merge, switchMap, tap, withLatestFrom } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class BuyerPrivacyPolicyFormFactory implements FormFactory {
  readonly #destroyRef = inject(DestroyRef);
  readonly #store = inject(Store);
  readonly #translateService = inject(TranslateService);
  readonly #dynamicFormFactory = inject(DynamicFormFactory);

  #initialState = {
    viewModel: {},
    dynamicFormFields: [],
    form: {
      formName: FormName.BuyerInformation,
      formGroup: null
    },
    person: null
  };

  #resetFormState$ = new BehaviorSubject(false);

  readonly state = toSignal(this.state$(), {
    initialValue: this.#initialState
  });

  resetFormState() {
    this.#resetFormState$.next(true);
  }

  private state$() {
    return merge(this.createFormState$(), this.resetFormState$()).pipe(takeUntilDestroyed(this.#destroyRef));
  }

  private createFormState$() {
    return combineLatest({
      resetFormState: this.#resetFormState$,
      exhibitionSettings: this.#store.select(getExhibitionSettings),
      isTranslationLoaded: this.#store.select(getIsTranslationLoaded),
      person: this.#store.select(selectBuyerPerson),
      isSelfRegistration: this.#store.select(getSelfRegistration),
      formFields: this.#store.select(selectBuyerPrivacyPolicyFormFields)
    }).pipe(
      filter(({ exhibitionSettings, isTranslationLoaded }) => !isEmpty(exhibitionSettings) && isTranslationLoaded),
      switchMap(({ exhibitionSettings, person, isSelfRegistration, formFields }) =>
        this.#translateService
          .stream([
            'privacy-policy-form.checkbox.privacy-link',
            'privacy-policy-form.info-message.privacy-link',
            'privacy-policy-form.checkbox.privacy-optional',
            'privacy-policy-form.info-message.privacy-optional'
          ])
          .pipe(
            map((translations) => ({
              exhibitionSettings,
              person,
              isSelfRegistration,
              formFields,
              translations
            }))
          )
      ),
      map(({ exhibitionSettings, person, isSelfRegistration, formFields, translations }) => {
        const { isPrivacyLinkVisible, isImprintVisible, isDisclaimerVisible, privacyUrl, disclaimerUrl, imprintUrl } =
          exhibitionSettings;

        const dynamicFormFields: DynamicFormFields = _filter([
          this.createPrivacyPolicyLink(exhibitionSettings, isSelfRegistration, formFields, translations),
          this.createPrivacyPolicyOptional(exhibitionSettings, isSelfRegistration, formFields, translations)
        ]);

        return {
          viewModel: {
            isPrivacyLinkVisible,
            isImprintVisible,
            isDisclaimerVisible,
            privacyUrl,
            disclaimerUrl,
            imprintUrl
          },
          dynamicFormFields,
          form: {
            formName: FormName.BuyerPrivacyPolicy,
            formGroup: this.#dynamicFormFactory.createForm(dynamicFormFields, formFields)
          },
          person
        };
      })
    );
  }

  private resetFormState$() {
    return this.#resetFormState$.pipe(
      withLatestFrom(this.#store.select(selectIsLogoutProcessing)),
      filter(([resetFormState, isLogoutProcessing]) => resetFormState && !isLogoutProcessing),
      map(() => this.#initialState),
      tap(() => this.#resetFormState$.next(false))
    );
  }

  private setPrivacyUrl(eventId: number, url: string) {
    if (url) {
      return url;
    }

    const dataProtectionUrl = `${window.location.origin}/data-protection`;

    return !isNil(eventId) ? `${dataProtectionUrl}/${eventId}` : dataProtectionUrl;
  }

  private createPrivacyPolicyLink(
    exhibitionSettings: ExhibitionSettingModel,
    isSelfRegistration: boolean,
    formFields: FormFields,
    translations: { [translationKey: string]: string }
  ): PrivacyPolicyCheckboxField {
    const { isPrivacyLinkMandatory, isPrivacyLinkVisible, privacyUrl, eventId } = exhibitionSettings;

    return isPrivacyLinkVisible
      ? {
          key: FieldKey.Disclaimer,
          type: FieldType.Checkbox,
          label: '',
          order: 1,
          isRequired: isPrivacyLinkMandatory,
          isHidden: false,
          isDisabled: false,
          isQuestionnaireField: false,
          columns: { xs: 12, sm: 12, md: 12, lg: 12, xl: 12 },
          options: [
            {
              key: OptionKey.DisclaimerConfirmation,
              label: this.setCheckboxLabelText(
                eventId,
                isSelfRegistration,
                translations,
                'privacy-policy-form.checkbox.privacy-link',
                privacyUrl
              ),
              value: !!formFields[FieldKey.Disclaimer]?.value,
              isRequired: isPrivacyLinkMandatory,
              isDisabled: false
            }
          ],
          text: {
            translation: translations['privacy-policy-form.info-message.privacy-link'],
            translationKey: 'privacy-policy-form.info-message.privacy-link',
            position: 'prepend'
          }
        }
      : null;
  }

  private createPrivacyPolicyOptional(
    exhibitionSettings: ExhibitionSettingModel,
    isSelfRegistration: boolean,
    formFields: FormFields,
    translations: { [translationKey: string]: string }
  ): PrivacyPolicyCheckboxField {
    const { isPrivacyOptionalMandatory, isPrivacyOptionalVisible, privacyOptionalUrl, eventId } = exhibitionSettings;

    return isPrivacyOptionalVisible
      ? {
          key: FieldKey.DisclaimerOptional,
          type: FieldType.Checkbox,
          label: '',
          order: 2,
          isRequired: isPrivacyOptionalMandatory,
          isHidden: false,
          isDisabled: false,
          isQuestionnaireField: false,
          columns: { xs: 12, sm: 12, md: 12, lg: 12, xl: 12 },
          options: [
            {
              key: OptionKey.DisclaimerOptionalConfirmation,
              label: this.setCheckboxLabelText(
                eventId,
                isSelfRegistration,
                translations,
                'privacy-policy-form.checkbox.privacy-optional',
                privacyOptionalUrl
              ),
              value: !!formFields[FieldKey.DisclaimerOptional]?.value,
              isRequired: isPrivacyOptionalMandatory,
              isDisabled: false
            }
          ],
          text: {
            translation: translations['privacy-policy-form.info-message.privacy-optional'],
            translationKey: 'privacy-policy-form.info-message.privacy-optional',
            position: 'append'
          }
        }
      : null;
  }

  private setCheckboxLabelText(
    eventId: number,
    isSelfRegistration: boolean,
    translations: { [translationKey: string]: string },
    translationKey: string,
    url: string
  ) {
    let labelText = translations[translationKey];

    if (!labelText) {
      return (
        AppConstants.MISSING_TRANSLATION + ' USE &lt;Privacy Policy&gt; in the translation as placeholder for a link'
      );
    }

    const replacingText = labelText.match(/<.*?>/);

    if (replacingText) {
      const linkTextOnly = head(replacingText);
      const cleanedLinkTextOnly = linkTextOnly.slice(1, -1);
      const text = isSelfRegistration
        ? cleanedLinkTextOnly
        : `<a href="${this.setPrivacyUrl(eventId, url)}" target="_blank">${cleanedLinkTextOnly}</a>`;

      labelText = labelText.replace(linkTextOnly, text);
    }

    return `<span data-translation="${translationKey}">${labelText}</span>`;
  }
}
